import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Table,
  Button,
  Input,
  Modal,
  Form,
  Select,
  message,
  Spin,
  Tag,
  Space,
  Card,
  Collapse,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LinkOutlined,
  UnlinkOutlined,
} from "@ant-design/icons";
import useZoneManagement from "../hooks/useZoneManagement";
import useAdminData from "../hooks/useAdminData";
import useLocationData from "../../../hooks/useLocationData";

const AdminZones = () => {
  const {
    zones,
    zoneDetail,
    availableSOs,
    zoneRestaurants,
    loading,
    fetchZones,
    fetchZoneDetail,
    createZone,
    updateZone,
    deleteZone,
    assignSMToZone,
    assignSOsToZone,
    fetchAvailableSOs,
    unassignSOFromZone,
    assignRestaurantsToSO,
    fetchZoneRestaurants,
    unassignRestaurantFromSO,
  } = useZoneManagement();

    const { supportManagers, availableBusinesses } = useAdminData();

    const userProfile = useSelector((state) => state.userProfile?.user);
    const userRole = userProfile?.role?.toUpperCase() || "";
    const isAdminOrSE = ["ADMIN", "SUPPORT_EXECUTIVE"].includes(userRole);

  const [searchText, setSearchText] = useState("");
  const [filteredZones, setFilteredZones] = useState([]);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [form] = Form.useForm();
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedZone, setSelectedZone] = useState(null);
  const [assignSMModalVisible, setAssignSMModalVisible] = useState(false);
  const [selectedSMIds, setSelectedSMIds] = useState([]);
  const [assignSOModalVisible, setAssignSOModalVisible] = useState(false);
  const [selectedSOIds, setSelectedSOIds] = useState([]);
  const [assignRestModalVisible, setAssignRestModalVisible] = useState(false);
  const [selectedRestaurants, setSelectedRestaurants] = useState([]);
  const [selectedSOForRest, setSelectedSOForRest] = useState(null);
  const [selectedLgas, setSelectedLgas] = useState([]);

  const {
    allStates,
    selectedState,
    setSelectedState,
    allLgas,
    loadingStates,
    loadingLgas,
  } = useLocationData();

  useEffect(() => {
    fetchZones();
  }, [fetchZones]);

  useEffect(() => {
    const filtered = (Array.isArray(zones) ? zones : []).filter(
      (zone) =>
        zone.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        zone.lga?.toLowerCase().includes(searchText.toLowerCase()) ||
        zone.state?.toLowerCase().includes(searchText.toLowerCase()),
    );
    setFilteredZones(filtered);
  }, [zones, searchText]);

  const getAvailableLgas = () => {
    const assignedLgas = zones
      .filter((z) => z.state === selectedState)
      .flatMap((z) =>
        Array.isArray(z.lgas) ? z.lgas : z.lgas ? [z.lgas] : [],
      );
    return (allLgas || []).filter((lga) => !assignedLgas.includes(lga.name));
  };

  const handleCreateZone = () => {
    setEditingZone(null);
    form.resetFields();
    setSelectedState(null);
    setSelectedLgas([]);
    setCreateModalVisible(true);
  };

  const handleEditZone = (zone) => {
    setEditingZone(zone);
    setSelectedState(zone.state);
    setSelectedLgas(
      Array.isArray(zone.lgas) ? zone.lgas : zone.lgas ? [zone.lgas] : [],
    );
    form.setFieldsValue({
      name: zone.name,
      state: zone.state,
      lgas: Array.isArray(zone.lgas) ? zone.lgas : zone.lgas ? [zone.lgas] : [],
    });
    setCreateModalVisible(true);
  };

  const handleDeleteZone = (zoneId) => {
    Modal.confirm({
      title: "Delete Zone",
      content: "Are you sure you want to delete this zone?",
      onOk: async () => {
        const result = await deleteZone(zoneId);
        if (result.success) {
          message.success("Zone deleted successfully");
        } else {
          message.error(result.error);
        }
      },
    });
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingZone) {
        const result = await updateZone(editingZone.id, values);
        if (result.success) {
          message.success("Zone updated successfully");
        } else {
          message.error(result.error);
        }
      } else {
        const result = await createZone(values);
        if (result.success) {
          message.success("Zone created successfully");
        } else {
          message.error(result.error);
        }
      }
      setCreateModalVisible(false);
    } catch (error) {
      message.error("Operation failed");
    }
  };

  const handleViewZone = async (zone) => {
    setSelectedZone(zone);
    await fetchZoneDetail(zone.id);
    await fetchZoneRestaurants(zone.id);
    setDetailModalVisible(true);
  };

  const handleAssignSM = (zone) => {
    setSelectedZone(zone);
    const existingIds = [];
    if (zone.supportManager?.id) existingIds.push(zone.supportManager.id);
    if (Array.isArray(zone.supportOfficers)) {
      zone.supportOfficers.forEach((so) => so.id && !existingIds.includes(so.id) && existingIds.push(so.id));
    }
    setSelectedSMIds(existingIds);
    setAssignSMModalVisible(true);
  };

  const handleAssignSMOk = async () => {
    if (!selectedSMIds || selectedSMIds.length === 0) {
      message.warning("Please select at least one support manager");
      return;
    }
    const result = await assignSMToZone(selectedZone.id, selectedSMIds);
    if (result.success) {
      message.success("Support managers assigned successfully");
      setAssignSMModalVisible(false);
      await fetchZones();
    } else {
      message.error(result.error);
    }
  };

  const handleAssignSOs = async (zone) => {
    setSelectedZone(zone);
    setSelectedSOIds(zone.supportOfficers?.map((so) => so.id) || []);
    await fetchAvailableSOs(zone.id);
    setAssignSOModalVisible(true);
  };

  const handleAssignSOsOk = async () => {
    if (selectedSOIds.length === 0) {
      message.warning("Please select at least one support officer");
      return;
    }
    const result = await assignSOsToZone(selectedZone.id, selectedSOIds);
    if (result.success) {
      message.success("Support officers assigned successfully");
      setAssignSOModalVisible(false);
      await fetchZones();
    } else {
      message.error(result.error);
    }
  };

  const handleAssignRestaurants = (zone, so) => {
    setSelectedZone(zone);
    setSelectedSOForRest(so);
    setSelectedRestaurants(so.assignedRestaurants?.map((r) => r.id) || []);
    setAssignRestModalVisible(true);
  };

  const handleAssignRestaurantsOk = async () => {
    if (selectedRestaurants.length === 0) {
      message.warning("Please select at least one restaurant");
      return;
    }
    const result = await assignRestaurantsToSO(
      selectedSOForRest.id,
      selectedRestaurants,
      selectedZone.id,
    );
    if (result.success) {
      message.success("Restaurants assigned successfully");
      setAssignRestModalVisible(false);
      await fetchZoneDetail(selectedZone.id);
    } else {
      message.error(result.error);
    }
  };

  const handleRemoveRestaurantFromSO = (soId, rtId) => {
    Modal.confirm({
      title: "Remove Restaurant",
      content: "Remove this restaurant from the support officer?",
      onOk: async () => {
        const result = await unassignRestaurantFromSO(
          soId,
          rtId,
          selectedZone.id,
        );
        if (result.success) {
          message.success("Restaurant removed successfully");
        } else {
          message.error(result.error);
        }
      },
    });
  };

  const handleRemoveSOFromZone = (soId) => {
    Modal.confirm({
      title: "Remove Support Officer",
      content: "Remove this support officer from the zone?",
      onOk: async () => {
        const result = await unassignSOFromZone(selectedZone.id, soId);
        if (result.success) {
          message.success("Support officer removed successfully");
          await fetchZoneDetail(selectedZone.id);
        } else {
          message.error(result.error);
        }
      },
    });
  };

  return (
    <div>
      <div className="mb-4 flex justify-between items-center">
        <Input.Search
          placeholder="Search zones..."
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
        {isAdminOrSE && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateZone}
          >
            Create Zone
          </Button>
        )}
      </div>

      <Spin spinning={loading}>
        {filteredZones.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-gray-500">No zones found</p>
          </div>
        ) : (
          <div className="mobile topup-data">
            <Collapse
              items={filteredZones.map((zone) => ({
                key: zone.id,
                label: (
                  <div className="flex justify-between items-center w-full">
                    <span className="font-semibold">{zone.name}</span>
                    <span className="text-gray-500">{zone.state}</span>
                  </div>
                ),
                children: (
                  <div className="space-y-4">
                    <div>
                      <p className="text-gray-600 mb-2">LGAs</p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {(Array.isArray(zone.lgas) ? zone.lgas : []).map(
                          (lga) => (
                            <Tag key={lga} color="cyan">
                              {lga}
                            </Tag>
                          ),
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">Support Manager</p>
                      <p className="font-medium">
                        {zone.supportManager ? (
                          <Tag color="blue">
                            {zone.supportManager.firstName}{" "}
                            {zone.supportManager.lastName}
                          </Tag>
                        ) : (
                          <Tag color="red">Unassigned</Tag>
                        )}
                      </p>
                    </div>
                    {/* <div>
                      <p className=" text-gray-600 mb-1">Support Officers</p>
                      <p className="font-medium">
                        {zone.supportOfficers?.length || 0} assigned
                      </p>
                    </div> */}
                    {isAdminOrSE && (
                      <Space>
                        <Button
                          type="primary"
                          size="small"
                          icon={<LinkOutlined />}
                          onClick={() => handleAssignSM(zone)}
                        >
                          Assign SM
                        </Button>
                        <Button
                          type="default"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => handleEditZone(zone)}
                        >
                          Edit
                        </Button>
                        <Button
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteZone(zone.id)}
                        >
                          Delete
                        </Button>
                      </Space>
                    )}
                  </div>
                ),
              }))}
              defaultActiveKey={[]}
            />
          </div>
        )}
      </Spin>

      <Modal
        title={editingZone ? "Edit Zone" : "Create Zone"}
        open={createModalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setCreateModalVisible(false);
          setSelectedState(null);
          setSelectedLgas([]);
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Zone Name"
            rules={[{ required: true, message: "Zone name is required" }]}
          >
            <Input placeholder="e.g., Lagos Zone 1" />
          </Form.Item>
          <Form.Item
            name="state"
            label="State"
            rules={[{ required: true, message: "State is required" }]}
          >
            <Select
              placeholder="Select a state"
              loading={loadingStates}
              options={(allStates || []).map((state) => ({
                label: state.name,
                value: state.name,
              }))}
              onChange={(value) => {
                setSelectedState(value);
                form.setFieldValue("state", value);
                setSelectedLgas([]);
                form.setFieldValue("lgas", []);
              }}
            />
          </Form.Item>
          <Form.Item
            name="lgas"
            label="LGAs (can select multiple)"
            rules={[
              { required: true, message: "At least one LGA is required" },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Select one or more LGAs"
              loading={loadingLgas}
              disabled={!selectedState}
              options={getAvailableLgas().map((lga) => ({
                label: lga.name,
                value: lga.name,
              }))}
              onChange={(values) => {
                setSelectedLgas(values);
                form.setFieldValue("lgas", values);
              }}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Assign Support Managers to Zone"
        open={assignSMModalVisible}
        onOk={handleAssignSMOk}
        onCancel={() => setAssignSMModalVisible(false)}
        width={700}
      >
        <Form layout="vertical">
          <Form.Item label="Support Managers">
            <Select
              mode="multiple"
              value={selectedSMIds}
              onChange={setSelectedSMIds}
              placeholder="Select support managers"
            >
              {supportManagers.map((manager) => (
                <Select.Option key={manager.id} value={manager.id}>
                  {manager.firstName} {manager.lastName} ({manager.email})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Assign Support Managers to Zone"
        open={assignSOModalVisible}
        onOk={handleAssignSOsOk}
        onCancel={() => setAssignSOModalVisible(false)}
        width={700}
      >
        <Form layout="vertical">
          <Form.Item label="Support Managers">
            <Select
              mode="multiple"
              value={selectedSOIds}
              onChange={setSelectedSOIds}
              placeholder="Select support managers"
            >
              {(availableSOs || []).map((so) => (
                <Select.Option key={so.id} value={so.id}>
                  {so.firstName} {so.lastName} ({so.email})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Assign Restaurants to ${selectedSOForRest?.firstName} ${selectedSOForRest?.lastName}`}
        open={assignRestModalVisible}
        onOk={handleAssignRestaurantsOk}
        onCancel={() => setAssignRestModalVisible(false)}
        width={700}
      >
        <Form layout="vertical">
          <Form.Item label="Restaurants">
            <Select
              mode="multiple"
              value={selectedRestaurants}
              onChange={setSelectedRestaurants}
              placeholder="Select restaurants"
            >
              {(availableBusinesses?.data || []).map((business) => (
                <Select.Option key={business.id} value={business.id}>
                  {business.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Zone Details: ${selectedZone?.name}`}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        width={900}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>,
        ]}
      >
        <Spin spinning={loading}>
          {zoneDetail && (
            <>
              <Card className="mb-4" bordered={false}>
                <p className="mb-2">
                  <strong>Zone:</strong> {zoneDetail.name}
                </p>
                <p className="mb-2">
                  <strong>State:</strong> {zoneDetail.state}
                </p>
                <p className="mb-2">
                  <strong>LGA:</strong> {zoneDetail.lga}
                </p>
                <p>
                  <strong>Support Manager:</strong>{" "}
                  {zoneDetail.supportManager ? (
                    <Tag color="blue">
                      {zoneDetail.supportManager.firstName}{" "}
                      {zoneDetail.supportManager.lastName}
                    </Tag>
                  ) : (
                    <Tag color="red">Unassigned</Tag>
                  )}
                </p>
              </Card>

              {(zoneDetail.supportOfficers || []).length > 0 && (
                <Card
                  title="Support Officers"
                  className="mb-4"
                  bordered={false}
                >
                  <Collapse
                    items={(zoneDetail.supportOfficers || []).map((so) => ({
                      key: so.id,
                      label: (
                        <div className="flex justify-between items-center w-full">
                          <span>
                            {so.firstName} {so.lastName} (
                            {so.assignedRestaurants?.length || 0} restaurants)
                          </span>
                        </div>
                      ),
                      children: (
                        <div>
                          {isAdminOrSE && (
                            <div className="mb-4">
                              <Button
                                type="primary"
                                size="small"
                                onClick={() =>
                                  handleAssignRestaurants(selectedZone, so)
                                }
                              >
                                Assign Restaurants
                              </Button>
                              <Button
                                danger
                                size="small"
                                style={{ marginLeft: "8px" }}
                                onClick={() => handleRemoveSOFromZone(so.id)}
                              >
                                Remove from Zone
                              </Button>
                            </div>
                          )}

                          {(so.assignedRestaurants || []).length > 0 ? (
                            <div>
                              {(so.assignedRestaurants || []).map((rt) => (
                                <div
                                  key={rt.id}
                                  className="flex justify-between items-center p-2 border rounded mb-2"
                                >
                                  <span>{rt.name}</span>
                                  {isAdminOrSE && (
                                    <Button
                                      type="text"
                                      danger
                                      size="small"
                                      icon={<LinkOutlined />}
                                      onClick={() =>
                                        handleRemoveRestaurantFromSO(
                                          so.id,
                                          rt.id,
                                        )
                                      }
                                    />
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500">
                              No restaurants assigned
                            </p>
                          )}
                        </div>
                      ),
                    }))}
                  />
                </Card>
              )}
            </>
          )}
        </Spin>
      </Modal>
    </div>
  );
};

export default AdminZones;
